"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import posthog from "posthog-js";
import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { ArrowRight } from "@/components/ui/icons/Arrow";
import { REGION_COLORS, REGION_FG } from "@/lib/regionColors";
import { AFRICA_VIEW_BOX, AFRICA_COUNTRIES } from "./africaMap";

type CardConfig = {
  key: string;
  name: string;
  tier: "feature" | "soon";
  comingSoon: boolean;
  /** Card background hex */
  bg: string;
  /** Card text/title colour hex — white on the two live (feature) cards on
   * dark/mid-tone backgrounds; dark ink on the three light "coming soon"
   * cards, which read better against their lighter mid-tone panels. */
  fg: string;
  /** Key into AFRICA_COUNTRIES for the highlighted country */
  country: string;
  /** Short description shown on the reveal panel — copy is final, verbatim from design handoff */
  copy: string;
  /** Where the "Explore <country>" link goes: /countries/<slug> for South
   * Africa (TRA-355 follow-up), /safari/botswana for Botswana's single
   * country-wide page (TRA-373). Unset cards keep today's back face
   * unchanged (just "Back"). */
  regionsHref?: string;
};

const CARDS: CardConfig[] = [
  // Live
  {
    key: "sa",
    name: "South Africa",
    tier: "feature",
    comingSoon: false,
    bg: "var(--bsa-canopy-700)", // Evergreen — the one card on a brand green, not a REGION_COLORS mid-tone
    fg: "#FFFFFF",
    country: "South Africa",
    copy: "From legendary Big Five reserves to more intimate wilderness areas, South Africa offers an unusually broad range of safari experiences. Lodges span rustic simplicity to exceptional luxury, with outstanding guiding, iconic wildlife encounters and remarkable variety between reserves.",
    regionsHref: "/countries/south-africa",
  },
  {
    key: "bw",
    name: "Botswana",
    tier: "feature",
    comingSoon: false,
    bg: REGION_COLORS.botswana,
    fg: "#FFFFFF",
    country: "Botswana",
    copy: "Botswana is defined by contrast. The vast Makgadikgadi Salt Pans give way to the waterways and floodplains of the Okavango Delta, while private concessions offer some of Africa's most distinctive wildlife experiences. Different landscapes create very different ways to safari.",
    regionsHref: "/safari/botswana",
  },
  // Coming soon
  {
    key: "zw",
    name: "Zimbabwe",
    tier: "soon",
    comingSoon: true,
    bg: REGION_COLORS.zimbabwe,
    fg: REGION_FG,
    country: "Zimbabwe",
    copy: "Zimbabwe has a safari character all its own: wild, understated and deeply authentic. Intimate camps and a strong safari tradition sit within extraordinary landscapes, from the elephant-rich plains of Hwange to Mana Pools, the Zambezi and Victoria Falls.",
  },
  {
    key: "zm",
    name: "Zambia",
    tier: "soon",
    comingSoon: true,
    bg: REGION_COLORS.zambia,
    fg: REGION_FG,
    country: "Zambia",
    copy: "Zambia is for travellers who want to get closer to the wilderness. The birthplace of the walking safari, it combines remote camps, exceptional guiding and abundant wildlife with a quieter, more immersive style of safari that feels wonderfully removed.",
  },
  {
    key: "na",
    name: "Namibia",
    tier: "soon",
    comingSoon: true,
    bg: REGION_COLORS.namibia,
    fg: REGION_FG,
    country: "Namibia",
    copy: "Namibia is less about conventional safari and more about scale, space and landscape. Vast deserts, sculpted mountains and desert-adapted wildlife create an experience unlike anywhere else, from Etosha's waterholes to the remote and haunting Skeleton Coast.",
  },
];

function RegionCard({
  card,
  index,
  isOpen,
  hasEntered,
  onToggle,
}: {
  card: CardConfig;
  index: number;
  isOpen: boolean;
  hasEntered: boolean;
  onToggle: (key: string) => void;
}) {
  const isFeature = card.tier === "feature";

  const toggle = () => onToggle(card.key);

  return (
    <div
      data-card-key={card.key}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onClick={toggle}
      onKeyDown={(e) => {
        // Don't let a keypress on a descendant (the "Explore <country>" link,
        // the hidden "Back" button) bubble up and toggle the card — only
        // the card's own focus ring should respond to Enter/Space.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
      className={[
        "regions-card",
        isFeature ? "regions-card--feature" : "regions-card--soon",
        hasEntered ? "in" : "",
        isOpen ? "is-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        {
          background: card.bg,
          color: card.fg,
          "--rc-bg": card.bg,
          animationDelay: `${index * 0.09}s`,
        } as React.CSSProperties
      }
    >
      {/* Full-continent map */}
      <div
        className={[
          "regions-card__map",
          isFeature ? "regions-card__map--feature" : "regions-card__map--soon",
        ].join(" ")}
        aria-hidden="true"
      >
        <svg
          viewBox={AFRICA_VIEW_BOX}
          style={{ width: "auto", height: "100%", display: "block", overflow: "visible" }}
        >
          {/* Base: all countries except the card's own */}
          <g className="regions-map-base">
            {Object.entries(AFRICA_COUNTRIES)
              .filter(([name]) => name !== card.country)
              .map(([name, d]) => (
                <path key={name} d={d} />
              ))}
          </g>
          {/* Highlight: the card's own country */}
          <path
            className="regions-map-hi"
            pathLength={1}
            d={AFRICA_COUNTRIES[card.country]}
          />
        </svg>
      </div>

      {/* Top — Coming soon pill */}
      <div className="regions-card__top">
        {card.comingSoon && (
          <span className="regions-badge">Coming soon</span>
        )}
      </div>

      {/* Bottom — country name + read more cue */}
      <div className="regions-card__body">
        <div className={["regions-card__name", isFeature ? "regions-card__name--feature" : "regions-card__name--soon"].join(" ")}>
          {card.name}
        </div>
        <span className="regions-card__cue" aria-hidden="true">
          Read more
          <ArrowRight size={15} />
        </span>
      </div>

      {/* Reveal panel */}
      <div className="rc-back">
        <div className="rc-panel">
          <div className="rc-back__map" aria-hidden="true">
            <svg viewBox={AFRICA_VIEW_BOX} style={{ width: "auto", height: "100%", display: "block" }}>
              <path d={AFRICA_COUNTRIES[card.country]} />
            </svg>
          </div>
          <div className="rc-back__name">{card.name}</div>
          <p className="rc-back__copy">{card.copy}</p>
          <div className="rc-back__foot">
            {card.regionsHref && (
              <Link
                href={card.regionsHref}
                className="rc-back__button rc-back__button--cta"
                tabIndex={isOpen ? 0 : -1}
                onClick={(e) => e.stopPropagation()}
              >
                Explore {card.name}
              </Link>
            )}
            <button
              type="button"
              className="rc-back__button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Regions() {
  const gridRef = useRef<HTMLDivElement>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Card keys that have played their entrance animation. Kept in React state
  // (not written via classList.add) because the card's className is now also
  // React-controlled (it toggles "is-open") — an imperative class write here
  // would get clobbered the next time React re-renders that className.
  //
  // The reduced-motion / no-IntersectionObserver cases are decided up front
  // via the lazy initializer (a synchronous read of platform capabilities,
  // not a subscription) so the effect below never needs to call setState
  // synchronously from its body — it only sets state from the async IO
  // callback, which is the supported "subscribe to an external system"
  // pattern.
  const [entered, setEntered] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    const skipsAnimation =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window);
    return skipsAnimation ? CARDS.map((c) => c.key) : [];
  });

  const markEntered = (key: string) => {
    setEntered((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  useEffect(() => {
    // Reduced-motion / no-IO-support: entrance state was already resolved by
    // the useState initializer above — nothing left to subscribe to.
    if (
      typeof window === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window)
    ) {
      return;
    }

    const grid = gridRef.current;
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll<HTMLElement>(".regions-card"));

    // Gate CSS animations behind .js-anim on the grid element. The grid's
    // className is static (never re-rendered by React with a different
    // value), so this imperative class is safe — unlike the per-card "in"
    // class above, it can never be clobbered by a React re-render.
    grid.classList.add("js-anim");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const key = (e.target as HTMLElement).dataset.cardKey;
            if (key) markEntered(key);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.25 },
    );

    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  // Escape closes whichever card is open, from anywhere on the page.
  useEffect(() => {
    if (openKey === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openKey]);

  const handleToggle = (key: string) => {
    setOpenKey((prev) => {
      const next = prev === key ? null : key;
      if (next !== null) {
        const card = CARDS.find((c) => c.key === next);
        if (card) posthog.capture("region_card_opened", { country: card.name });
      }
      return next;
    });
  };

  return (
    <Section id="regions" pad="compact" className="scroll-mt-24 bg-surface">
      <Container>
        <SectionHeading
          align="center"
          className="mb-6"
          titleClassName="font-heading"
          title={
            <>
              Five Countries.{" "}
              <span className="font-editorial font-normal italic text-primary">
                One trusted standard.
              </span>
            </>
          }
          body="Every lodge is evaluated against the same expert-led criteria, making comparisons meaningful across our growing collection."
        />

        <div
          ref={gridRef}
          className="regions-grid grid gap-[18px]"
        >
          {CARDS.map((card, i) => (
            <RegionCard
              key={card.key}
              card={card}
              index={i}
              isOpen={openKey === card.key}
              hasEntered={entered.includes(card.key)}
              onToggle={handleToggle}
            />
          ))}
        </div>
      </Container>
    </Section>
  );
}
